import React, { useLayoutEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

interface MaskedRevealProps {
  children: React.ReactNode;
  delay?: number;
  duration?: number;
  once?: boolean;
  className?: string;
}

const MaskedReveal: React.FC<MaskedRevealProps> = ({
  children,
  delay = 0,
  duration = 0.72,
  once = true,
  className,
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = useReducedMotion();
  const [isVisible, setIsVisible] = useState(true);

  useLayoutEffect(() => {
    const element = elementRef.current;

    if (!element || shouldReduceMotion) {
      setIsVisible(true);
      return;
    }

    const rect = element.getBoundingClientRect();
    const isInViewport = rect.top < window.innerHeight * 0.92 && rect.bottom > 0;

    if (isInViewport) {
      setIsVisible(false);
      const frame = window.requestAnimationFrame(() => setIsVisible(true));
      return () => window.cancelAnimationFrame(frame);
    }

    if (rect.bottom <= 0) {
      setIsVisible(true);
      return;
    }

    setIsVisible(false);

    let observer: IntersectionObserver | undefined;
    const revealIfVisible = () => {
      const currentRect = element.getBoundingClientRect();
      const hasEntered =
        currentRect.top < window.innerHeight * 0.92 && currentRect.bottom > 0;

      if (hasEntered) {
        setIsVisible(true);
        if (once) {
          observer?.disconnect();
          window.removeEventListener("scroll", revealIfVisible);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    };

    if ("IntersectionObserver" in window) {
      observer = new IntersectionObserver(revealIfVisible, {
        threshold: 0.05,
        rootMargin: "0px 0px -8% 0px",
      });
      observer.observe(element);
    }

    // The scroll listener is a small fallback for browsers where an observer
    // misses an element after a large or programmatic scroll jump.
    window.addEventListener("scroll", revealIfVisible, { passive: true });
    revealIfVisible();

    return () => {
      observer?.disconnect();
      window.removeEventListener("scroll", revealIfVisible);
    };
  }, [once, shouldReduceMotion]);

  return (
    <motion.div
      ref={elementRef}
      className={className}
      initial={false}
      animate={
        shouldReduceMotion || isVisible
          ? { opacity: 1, y: 0, clipPath: "inset(0 0 0% 0)" }
          : { opacity: 0, y: 36, clipPath: "inset(0 0 100% 0)" }
      }
      transition={{
        duration: shouldReduceMotion ? 0 : duration,
        delay: shouldReduceMotion || !isVisible ? 0 : delay,
        ease: [0.22, 1, 0.36, 1],
      }}
    >
      {children}
    </motion.div>
  );
};

export default MaskedReveal;
